import { Icon } from '@/components/ui';
import type { DoubleBannerFragment, DoubleBannerItem } from '@/lib/contentful/types';
import { getImageUrl } from '@/lib/contentful/utils';
import clsx from 'clsx';
import Image from 'next/image';
import { Link } from '@/lib/i18n/navigation';
import styles from './DoubleBanner.module.scss';

export interface DoubleBannerProps {
  /** Contentful DoubleBanner data */
  data: DoubleBannerFragment;
  /** Optional className */
  className?: string;
  /** CMS component index for E2E test targeting */
  cmsIndex?: number;
}

/**
 * Individual banner panel
 */
function BannerPanel({
  banner,
  position,
  testIdPrefix,
}: {
  banner: DoubleBannerItem;
  position: 'left' | 'right';
  testIdPrefix?: string;
}) {
  const bgImageUrl = getImageUrl(banner.backgroundImage);
  const panelTestId = testIdPrefix ? `${testIdPrefix}-${position}` : undefined;

  const content = (
    <>
      {/* Background */}
      {bgImageUrl && (
        <Image
          src={bgImageUrl}
          alt=""
          fill
          className={styles.background}
          sizes="(max-width: 768px) 100vw, 50vw"
        />
      )}

      {/* Content */}
      <div className={styles.content}>
        {banner.label && (
          <span
            className={styles.label}
            data-testid={panelTestId ? `${panelTestId}-label` : undefined}
          >
            {banner.label}
          </span>
        )}
        {banner.heading && (
          <h3
            className={styles.heading}
            data-testid={panelTestId ? `${panelTestId}-title` : undefined}
          >
            {banner.heading}
          </h3>
        )}
        {banner.content && (
          <p
            className={styles.description}
            data-testid={panelTestId ? `${panelTestId}-body` : undefined}
          >
            {banner.content}
          </p>
        )}
        {banner.ctaText && (
          <span className={styles.cta} data-testid={panelTestId ? `${panelTestId}-cta` : undefined}>
            {banner.ctaText}
            <Icon name="arrow-right" size={14} className={styles.ctaIcon} />
          </span>
        )}
      </div>
    </>
  );

  if (banner.ctaUrl) {
    return (
      <Link
        href={banner.ctaUrl}
        className={clsx(styles.banner, styles[position])}
        data-testid={panelTestId}
      >
        {content}
      </Link>
    );
  }

  return (
    <div className={clsx(styles.banner, styles[position])} data-testid={panelTestId}>
      {content}
    </div>
  );
}

/**
 * Side-by-side promotional banners
 * Each banner can have background image, text, and CTA link
 *
 * Accepts Contentful DoubleBanner fragment data
 */
export function DoubleBanner({ data, className, cmsIndex }: DoubleBannerProps) {
  const leftBanner = data.leftBanner;
  const rightBanner = data.rightBanner;

  // Generate testid prefix for E2E tests
  const testIdPrefix = cmsIndex !== undefined ? `doublebanner-${cmsIndex}` : undefined;

  if (!leftBanner && !rightBanner) {
    return null;
  }

  return (
    <section className={clsx(styles.section, className)} data-testid={testIdPrefix}>
      <div className={styles.grid}>
        {leftBanner && (
          <BannerPanel banner={leftBanner} position="left" testIdPrefix={testIdPrefix} />
        )}
        {rightBanner && (
          <BannerPanel banner={rightBanner} position="right" testIdPrefix={testIdPrefix} />
        )}
      </div>
    </section>
  );
}

DoubleBanner.displayName = 'DoubleBanner';
